import {
  Avatar,
  Badge,
  Box,
  Flex,
  Icon,
  Image,
  keyframes,
  Link,
  List,
  ListItem,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Text,
  HStack,
  Tooltip,
} from '@chakra-ui/react'
import { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import { LuChevronLeft, LuChevronRight, LuClipboardList, LuPlus, LuUser, LuLogOut } from 'react-icons/lu'
import sideBar from '../../assets/sideBarLogo.png'
import { NavLink, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { useLogOut } from '../../api/api_hooks/auth/useLogOut'
import { useMyProfile } from '../../api/api_hooks/user/myProfile'
import LanguageButton from '../_shared/LanguageButton'
import CreditBadge from '../_shared/CreditBadge'
import { useTranslation } from 'react-i18next'
import { useUserModules } from '../../contexts/UserModuleContext'
import { MODULE_LABEL_KEYS } from '../../config/moduleConfig'
import { useLabAppAccess } from '../../hooks/useLabAppAccess'
import { LAB_APPS, labAppPath } from '../../config/labApps'
import { PlanType } from '../../types/subscription'
import { useAuthedImage } from '../../hooks/useAuthedImage'

export default function ModularSideBar() {
  const { data: userData } = useMyProfile()
  const avatarSrc = useAuthedImage(userData?.avatarFilePath)
  const location = useLocation()
  const { availableModules } = useUserModules()
  const { isAppEnabled } = useLabAppAccess()

  /**
   * Les onglets de navigation : les modules de l'utilisateur, plus le registre
   * d'évaluation. Celui-ci n'est pas un module — pas de type en base, pas
   * d'interrupteur admin — mais une vue transversale sur les cas Western
   * blot : tous les cas lancés, filtres par pathologie, et l'accord
   * paillasse / IRIS agrégé. D'où une entrée statique plutôt qu'un ModuleType
   * de plus, qui exigerait une migration de l'ENUM Postgres.
   */
  const navItems = useMemo(() => {
    const mapped = availableModules.map((m) => ({
      ariaLabel: m.ariaLabel,
      href: m.href,
      icon: m.icon,
      labelKey: MODULE_LABEL_KEYS[m.type],
      isNew: false,
    }))
    const registre = {
      ariaLabel: 'registre',
      href: '/lab/registry',
      icon: <Icon as={LuClipboardList} boxSize={6} strokeWidth={2} />,
      labelKey: 'modules.blotRegistry',
      // Le temps que l'onglet soit connu des pilotes ; à retirer ensuite.
      isNew: true,
    }
    // L'admin ferme TOUJOURS la liste, après le registre : c'est l'entrée
    // outillage, pas un module de travail — elle ne doit pas s'intercaler
    // dans le parcours quotidien.
    const admin = mapped.filter((i) => i.ariaLabel === 'admin')
    const rest = mapped.filter((i) => i.ariaLabel !== 'admin')
    return [...rest, registre, ...admin]
  }, [availableModules])
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { mutate: signOut, isSuccess } = useLogOut()
  const [isExpanded, setIsExpanded] = useState(() => {
    try { return localStorage.getItem('sidebar_expanded') !== 'false' } catch (_e) { return true }
  })
  const [spinLogo, setSpinLogo] = useState(false)

  const toggleSidebar = useCallback(() => {
    setIsExpanded((prev) => {
      const next = !prev
      try { localStorage.setItem('sidebar_expanded', String(next)) } catch (_e) {}
      return next
    })
  }, [])
  const [spinKey, setSpinKey] = useState(0)
  const spinTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const spinAnimation = useMemo(() => keyframes`
    0% { transform: perspective(800px) rotateY(0deg); }
    30% { transform: perspective(800px) rotateY(720deg); }
    100% { transform: perspective(800px) rotateY(1080deg); }
  `, [])

  // Slow "breathing" halo behind the logo — makes the panel feel like a live
  // system rather than a static frame.
  const haloBreathe = useMemo(() => keyframes`
    0%, 100% { opacity: 0.45; transform: scale(0.92); }
    50%      { opacity: 0.85; transform: scale(1.06); }
  `, [])
  // Soft neon pulse ring on the collapse handle.
  const ringPulse = useMemo(() => keyframes`
    0%, 100% { box-shadow: 0 2px 12px rgba(0,0,0,0.4), 0 0 0 0 rgba(167,139,250,0.35); }
    50%      { box-shadow: 0 2px 16px rgba(0,0,0,0.5), 0 0 0 4px rgba(167,139,250,0); }
  `, [])

  useEffect(() => {
    if (location.pathname === '/') {
      setSpinLogo(true)
      setSpinKey((k) => k + 1)
      spinTimeoutRef.current = setTimeout(() => setSpinLogo(false), 900)
      return () => {
        if (spinTimeoutRef.current) clearTimeout(spinTimeoutRef.current)
      }
    }
    setSpinLogo(false)
  }, [location.pathname])

  // Custom logic to determine if a menu item should be active
  const isMenuItemActive = useCallback((href: string): boolean => {
    const currentPath = location.pathname

    // Special case: /lab should NOT be active when on /lab-analysis/blot
    if (href === '/lab' && currentPath.startsWith('/lab-analysis/blot')) {
      return false
    }

    // Le registre d'évaluation a son propre onglet : /lab ne s'allume pas
    // pour lui, sinon deux entrées brillent en même temps.
    if (href === '/lab' && currentPath.startsWith('/lab/registry')) {
      return false
    }

    // For other cases, use standard path matching
    return currentPath === href || currentPath.startsWith(`${href}/`)
  }, [location.pathname])


  // Memoize active indicator styles
  const activeIndicatorStyle = useMemo(() => ({
    width: '4px',
    content: "''",
    position: 'absolute' as const,
    right: '-16px',
    background: 'linear-gradient(180deg, #a78bfa 0%, #22d3ee 100%)',
    height: '60%',
    top: '20%',
    borderRadius: '8px 0px 0px 8px',
    boxShadow: '0 0 12px rgba(167,139,250,0.8), 0 0 6px rgba(34,211,238,0.5)',
  }), [])

  if (isSuccess) return <Navigate to="/login" />

  return (
    <Flex
      p="32px 16px 24px"
      color={'white'}
      direction={'column'}
      height={'100%'}
      position="relative"
      width={isExpanded ? '240px' : '96px'}
      transition="width 0.25s cubic-bezier(0.4, 0, 0.2, 1)"
    >
      {/* Toggle button */}
      <Box
        as="button"
        onClick={toggleSidebar}
        position="absolute"
        top="50%"
        right="-11px"
        transform="translateY(-50%)"
        w="22px"
        h="22px"
        borderRadius="full"
        bg="linear-gradient(135deg, rgba(40,24,66,0.85), rgba(15,10,26,0.85))"
        sx={{ backdropFilter: 'blur(6px)', animation: `${ringPulse} 3.2s ease-in-out infinite` }}
        border="1px solid"
        borderColor="rgba(167,139,250,0.35)"
        color="rgba(167,139,250,0.85)"
        display="flex"
        alignItems="center"
        justifyContent="center"
        zIndex={10}
        transition="all 0.2s"
        _hover={{
          borderColor: 'rgba(34,211,238,0.6)',
          color: 'cyan.200',
          boxShadow: '0 2px 16px rgba(0,0,0,0.5), 0 0 14px rgba(34,211,238,0.3)',
          transform: 'translateY(-50%) scale(1.12)',
        }}
        cursor="pointer"
        aria-label={isExpanded ? t('menu.collapseSidebar') : t('menu.expandSidebar')}
      >
        <Icon
          as={isExpanded ? LuChevronLeft : LuChevronRight}
          boxSize={3.5}
        />
      </Box>

      <Box display="flex" justifyContent="center" alignItems="center" mb={6} position="relative">
        {/* Breathing neon halo behind the logo */}
        <Box
          position="absolute"
          w="120px"
          h="120px"
          borderRadius="full"
          pointerEvents="none"
          bg="radial-gradient(circle, rgba(167,139,250,0.35) 0%, rgba(34,211,238,0.12) 45%, transparent 70%)"
          sx={{ animation: `${haloBreathe} 5s ease-in-out infinite`, filter: 'blur(4px)' }}
        />
        <Link
          href="/"
          position="relative"
          zIndex={1}
          onClick={(e) => {
            e.preventDefault()
            setSpinLogo(true)
            setSpinKey((k) => k + 1)
            if (spinTimeoutRef.current) clearTimeout(spinTimeoutRef.current)
            spinTimeoutRef.current = setTimeout(() => {
              setSpinLogo(false)
              navigate('/')
            }, 800)
          }}
        >
          <Image
            src={sideBar}
            alt=""
            maxW="64px"
            animation={spinLogo ? `${spinAnimation} 1.2s cubic-bezier(0.5, 0, 0.2, 1) forwards` : 'none'}
            style={{ backfaceVisibility: 'hidden', transformStyle: 'preserve-3d' }}
            key={spinKey}
            onAnimationEnd={() => setSpinLogo(false)}
          />
        </Link>
      </Box>
      {/* Gradient hairline instead of a flat divider */}
      <Box
        h="1px"
        mb={6}
        bgGradient="linear(to-r, transparent, rgba(167,139,250,0.45) 35%, rgba(34,211,238,0.35) 65%, transparent)"
      />
      <Box
        flex="1"
        overflowY="auto"
        overflowX="hidden"
        mb={4}
        css={{
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'linear-gradient(180deg, rgba(167,139,250,0.6), rgba(34,211,238,0.5))',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'linear-gradient(180deg, rgba(167,139,250,0.85), rgba(34,211,238,0.75))',
          },
        }}
      >
        <List spacing={3} data-tour-id="sidebar-nav">
          {navItems.map((moduleConfig) => (
            <ListItem key={moduleConfig.ariaLabel}>
              <Tooltip
                label={!isExpanded ? t(moduleConfig.labelKey) : ''}
                placement="right"
                hasArrow
                bg="white"
                color="brand.purple"
                borderRadius="lg"
                px={3}
                py={2}
                fontSize="sm"
                fontWeight="medium"
              >
              <NavLink aria-label={moduleConfig.ariaLabel} to={moduleConfig.href}>
                {() => {
                  const isActive = isMenuItemActive(moduleConfig.href)
                  return (
                  <HStack
                    spacing={isExpanded ? 3 : 0}
                    p={2}
                    borderRadius="xl"
                    position="relative"
                    justify={isExpanded ? 'flex-start' : 'center'}
                    // Active = neon-glass chip: translucent purple→cyan fill, a thin
                    // gradient-ish border and a soft dual glow. Inactive = transparent.
                    background={isActive
                      ? 'linear-gradient(135deg, rgba(167,139,250,0.26), rgba(34,211,238,0.12))'
                      : 'transparent'}
                    color="white"
                    border="1px solid"
                    borderColor={isActive ? 'rgba(167,139,250,0.45)' : 'transparent'}
                    transition="all 0.22s cubic-bezier(0.4, 0, 0.2, 1)"
                    align="center"
                    boxShadow={isActive
                      ? '0 0 22px rgba(167,139,250,0.30), 0 0 40px rgba(34,211,238,0.10), inset 0 1px 0 rgba(255,255,255,0.12)'
                      : 'none'}
                    sx={{
                      backdropFilter: isActive ? 'blur(8px)' : 'none',
                      '& svg': {
                        color: isActive ? 'white' : 'rgba(255,255,255,0.72)',
                        filter: isActive ? 'drop-shadow(0 0 6px rgba(167,139,250,0.75))' : 'none',
                        transition: 'filter 0.22s, transform 0.22s',
                      },
                      'path,>*': {
                        stroke: isActive ? 'white' : 'rgba(255,255,255,0.72)',
                        fill: isActive ? 'white' : 'rgba(255,255,255,0.72)',
                      },
                      '::after': isActive ? activeIndicatorStyle : '',
                    }}
                    _hover={{
                      background: isActive
                        ? 'linear-gradient(135deg, rgba(167,139,250,0.30), rgba(34,211,238,0.16))'
                        : 'rgba(167,139,250,0.10)',
                      borderColor: isActive ? 'rgba(34,211,238,0.55)' : 'rgba(167,139,250,0.28)',
                      boxShadow: isActive
                        ? '0 0 28px rgba(167,139,250,0.38), 0 0 48px rgba(34,211,238,0.14)'
                        : '0 0 14px rgba(167,139,250,0.12)',
                      transform: isExpanded ? 'translateX(2px)' : 'none',
                      '& svg': {
                        color: 'white',
                        filter: 'drop-shadow(0 0 6px rgba(167,139,250,0.7))',
                        transform: 'scale(1.06)',
                      },
                      'path,>*': { stroke: 'white', fill: 'white' },
                    }}
                  >
                    <Box
                      w="48px"
                      h="48px"
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      flexShrink={0}
                      position="relative"
                      // Soft halo behind the active icon.
                      _before={isActive ? {
                        content: '""',
                        position: 'absolute',
                        inset: '6px',
                        borderRadius: 'full',
                        background: 'radial-gradient(circle, rgba(167,139,250,0.28), transparent 70%)',
                        pointerEvents: 'none',
                      } : undefined}
                    >
                      {moduleConfig.icon}
                      {/* Nouveauté, version repliée : un point qui se voit
                          sans libellé. */}
                      {moduleConfig.isNew && !isExpanded && (
                        <Box
                          position="absolute"
                          top="8px"
                          right="8px"
                          w="8px"
                          h="8px"
                          borderRadius="full"
                          bg="cyan.300"
                          boxShadow="0 0 8px rgba(34,211,238,0.95), 0 0 2px rgba(34,211,238,1)"
                          sx={{
                            animation: 'registryNewPulse 1.8s ease-in-out infinite',
                            '@keyframes registryNewPulse': {
                              '0%, 100%': { transform: 'scale(1)', opacity: 1 },
                              '50%': { transform: 'scale(0.75)', opacity: 0.7 },
                            },
                          }}
                        />
                      )}
                    </Box>
                    <Text
                      fontSize="md"
                      fontWeight="500"
                      whiteSpace="nowrap"
                      opacity={isExpanded ? 1 : 0}
                      width={isExpanded ? 'auto' : 0}
                      overflow="hidden"
                      flexShrink={0}
                      transition="opacity 0.2s, width 0.2s"
                    >
                      {t(moduleConfig.labelKey)}
                    </Text>
                    {moduleConfig.isNew && isExpanded && (
                      <Badge
                        colorScheme="cyan"
                        variant="solid"
                        fontSize="2xs"
                        px={1}
                        py={0.5}
                        ml={-1}
                        borderRadius="md"
                        flexShrink={0}
                        boxShadow="0 0 10px rgba(34,211,238,0.45)"
                      >
                        Nouveau
                      </Badge>
                    )}
                  </HStack>
                  )
                }}
              </NavLink>
            </Tooltip>
            </ListItem>
          ))}
        </List>
      </Box>

      {/* ── Accès rapide ── */}
      <Box mb={3}>
        <Menu placement="right" closeOnSelect>
          <Tooltip
            label={!isExpanded ? t('menu.quickAccess') : ''}
            placement="right"
            hasArrow
            bg="white"
            color="brand.purple"
            borderRadius="lg"
            px={3}
            py={2}
            fontSize="sm"
          >
            <MenuButton as={Box} cursor="pointer" w="100%" data-tour-id="sidebar-quick">
              <HStack
                spacing={isExpanded ? 3 : 0}
                p={2}
                borderRadius="lg"
                justify={isExpanded ? 'flex-start' : 'center'}
                color="white"
                transition="all 0.25s cubic-bezier(0.4, 0, 0.2, 1)"
                _hover={{
                  background: 'white',
                  color: 'brand.purple',
                  '& svg': { color: 'brand.purple' },
                  'path,>*': { stroke: 'brand.purple', fill: 'brand.purple' },
                }}
                sx={{
                  '& svg': { color: 'white' },
                  'path,>*': { stroke: 'white', fill: 'white' },
                }}
              >
                <Box
                  w="48px"
                  h="48px"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  flexShrink={0}
                >
                  <Icon as={LuPlus} boxSize={5} />
                </Box>
                <Text
                  fontSize="md"
                  fontWeight="500"
                  whiteSpace="nowrap"
                  opacity={isExpanded ? 1 : 0}
                  width={isExpanded ? 'auto' : 0}
                  overflow="hidden"
                  transition="opacity 0.2s, width 0.2s"
                >
                  Accès rapide
                </Text>
              </HStack>
            </MenuButton>
          </Tooltip>
          <Portal>
            <MenuList
              p={3}
              borderRadius="2xl"
              boxShadow="0 20px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.08)"
              border="1px solid"
              borderColor="gray.100"
              minW="230px"
              bg="white"
            >
              {/* Header */}
              <Box px={2} pb={2} mb={1}>
                <Text fontSize="xs" fontWeight="semibold" color="gray.400" letterSpacing="0.08em" textTransform="uppercase">
                  Accès rapide
                </Text>
              </Box>

              {/* Le registre du Lab Studio fournit les entrées : une
                  application ajoutée là apparaît ici d'elle-même, une
                  application coupée dans l'administration en sort — ce
                  raccourci est un lanceur, pas une vitrine. */}
              {LAB_APPS
                .filter((app) => isAppEnabled(app.id))
                .map((app) => ({
                  icon: app.icon,
                  label: `Analyse ${app.label}`,
                  sub: app.tagline,
                  path: labAppPath(app.id),
                  bg: `${app.colorScheme}.50`,
                  iconBg: `${app.colorScheme}.100`,
                  iconColor: `${app.colorScheme}.600`,
                }))
                .map(({ icon, label, sub, path, bg, iconBg, iconColor }) => (
                <MenuItem
                  key={path}
                  onClick={() => navigate(path)}
                  borderRadius="xl"
                  px={3}
                  py={2.5}
                  mb={1}
                  _last={{ mb: 0 }}
                  _hover={{ bg }}
                  transition="all 0.15s"
                >
                  <HStack spacing={3} align="center">
                    <Box
                      w="36px"
                      h="36px"
                      borderRadius="lg"
                      bg={iconBg}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      flexShrink={0}
                    >
                      <Icon as={icon} boxSize={4} color={iconColor} />
                    </Box>
                    <Box>
                      <Text fontSize="sm" fontWeight="600" color="gray.800" lineHeight="1.2">
                        {label}
                      </Text>
                      <Text fontSize="xs" color="gray.400" lineHeight="1.3">
                        {sub}
                      </Text>
                    </Box>
                  </HStack>
                </MenuItem>
              ))}
            </MenuList>
          </Portal>
        </Menu>
      </Box>

      <Box display="flex" justifyContent="center" alignItems="center" mb={4}>
        <CreditBadge variant="compact" />
      </Box>
      <Box display="flex" justifyContent="center" alignItems="center" mb={4}>
        <LanguageButton color="white" />
      </Box>

      <Box h="1px" bg="linear-gradient(90deg, transparent, rgba(167,139,250,0.2), transparent)" my={3} />

      {/* ── Avatar + plan ── */}
      <Menu placement="right-end">
        <MenuButton
          data-tour-id="sidebar-user"
          as={Box}
          cursor="pointer"
          _hover={{ opacity: 0.85 }}
          transition="opacity 0.2s"
          px={2}
          py={1.5}
          borderRadius="xl"
        >
          <HStack
            spacing={isExpanded ? 3 : 0}
            justify={isExpanded ? 'flex-start' : 'center'}
            align="center"
          >
            <Avatar
              size="sm"
              name={userData?.contactName || ''}
              src={avatarSrc ?? ''}
              flexShrink={0}
              bg="whiteAlpha.400"
              color="white"
              fontWeight="bold"
            />
            <Box
              opacity={isExpanded ? 1 : 0}
              w={isExpanded ? 'auto' : 0}
              overflow="hidden"
              transition="opacity 0.2s, width 0.2s"
            >
              <Text
                fontSize="sm"
                fontWeight="600"
                color="white"
                whiteSpace="nowrap"
                lineHeight="1.2"
              >
                {userData?.contactName?.split(' ')[0] ?? ''}
              </Text>
              {userData?.plan && userData.plan !== PlanType.FREE && (
                <Text
                  fontSize="2xs"
                  color={userData.plan === PlanType.PREMIUM ? 'yellow.200' : 'whiteAlpha.600'}
                  fontWeight="semibold"
                  letterSpacing="0.05em"
                  whiteSpace="nowrap"
                >
                  {userData.plan === PlanType.PREMIUM ? '✦ Premium'
                    : userData.plan === PlanType.ENTERPRISE ? '✦ Institution'
                    : 'Essentiel'}
                </Text>
              )}
            </Box>
          </HStack>
        </MenuButton>
        <Portal>
          <MenuList
            p={3}
            borderRadius="2xl"
            boxShadow="0 20px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.08)"
            border="1px solid"
            borderColor="gray.100"
            minW="210px"
            bg="white"
          >
            {/* User info header */}
            <Box px={2} pb={3} mb={1} borderBottom="1px solid" borderColor="gray.100">
              <Text fontSize="sm" fontWeight="700" color="gray.800" noOfLines={1}>
                {t('shared.common.dr')} {userData?.contactName}
              </Text>
              <Text fontSize="xs" color="gray.400" noOfLines={1} mt={0.5}>
                {userData?.email}
              </Text>
            </Box>

            <MenuItem
              onClick={() => navigate('/my-info')}
              borderRadius="xl"
              px={3}
              py={2.5}
              mb={1}
              _hover={{ bg: 'purple.50' }}
              transition="all 0.15s"
            >
              <HStack spacing={3}>
                <Box w="34px" h="34px" borderRadius="lg" bg="purple.100"
                  display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
                  <Icon as={LuUser} boxSize={4} color="purple.600" />
                </Box>
                <Box>
                  <Text fontSize="sm" fontWeight="600" color="gray.800" lineHeight="1.2">
                    {t('menu.seeProfile')}
                  </Text>
                  <Text fontSize="xs" color="gray.400">{t('menu.settingsAndPlan')}</Text>
                </Box>
              </HStack>
            </MenuItem>

            <MenuItem
              onClick={() => signOut()}
              borderRadius="xl"
              px={3}
              py={2.5}
              _hover={{ bg: 'red.50' }}
              transition="all 0.15s"
            >
              <HStack spacing={3}>
                <Box w="34px" h="34px" borderRadius="lg" bg="red.100"
                  display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
                  <Icon as={LuLogOut} boxSize={4} color="red.500" />
                </Box>
                <Text fontSize="sm" fontWeight="600" color="red.600" lineHeight="1.2">
                  {t('menu.logOut')}
                </Text>
              </HStack>
            </MenuItem>
          </MenuList>
        </Portal>
      </Menu>
    </Flex>
  )
}
